import { InstanceBatch } from "../../core/batching/instance_batch.js";
import { AttributeSize, BlendMode } from "../../core/types/webgl_types.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { CatmulromPrecomputed } from "../../utils/catmulrom_precomputed.js";
import { max2 } from "../../../common/common_funcs.js";

export const CoinFlyLayout = {
  a_position: AttributeSize.Vec2,
  a_size: AttributeSize.Float,
  a_angle: AttributeSize.Float,
  a_frame: AttributeSize.Float,
  a_timing: AttributeSize.Vec2,
} as const;

export class VfxCoinFlyBatch extends InstanceBatch<typeof CoinFlyLayout>
{
  public static readonly MAX_INSTANCES      : number = 120;
  public static readonly ATLAS_COLS         : number = 4;
  public static readonly ATLAS_ROWS         : number = 4;
  public static readonly IS_COLUMN_MAJOR    : boolean = true;
  public static readonly SPIN_ANIMATION_FPS : number = 20;

  private precomputed_list : CatmulromPrecomputed[] = [];
  private anim_offsets     : number[] = [];
  private ease_funcs       : ((( t: number ) => number) | undefined)[] = [];

  constructor()
  {
    super( {
      max_instances: VfxCoinFlyBatch.MAX_INSTANCES,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      vertices_per_instance: 6,
      blend_mode: BlendMode.Normal,
      layout: CoinFlyLayout,
    } );

    this.buildShader( new ShaderBuilder()
      .withUniform( 'sampler2D', 'u_texture' )
      .withUniform( 'float', 'u_scale' )
      .includeChunk( 'getSpriteUV' )
      .includeChunk( 'remap' )
      .withVarying( 'vec2', 'v_uv' )
      .withVarying( 'float', 'v_alpha' )
      .withVertexLogic( `
        float elapsed = max( 0.0, u_time - a_timing.x );
        float t = clamp( elapsed / a_timing.y, 0.0, 1.0 );
        
        position = a_position;
        angle = a_angle;
        size = vec2( a_size * u_scale );

        vec2 min_uv;
        vec2 max_uv;

        getSpriteUV(
          int( a_frame + 0.5 ),
          vec2( textureSize( u_texture, 0 ) ),
          ivec2( ${VfxCoinFlyBatch.ATLAS_COLS}, ${VfxCoinFlyBatch.ATLAS_ROWS} ),
          vec2( 0.0 ),
          min_uv,
          max_uv
        );

        v_uv = mix( min_uv, max_uv, vec2( uv.x, 1.0 - uv.y ) );
        
        // Smooth fade out near the end of life (from 0.85 to 1.0 progress)
        v_alpha = ( u_time < a_timing.x ) ? 0.0 : remap( t, 0.85, 1.0, 1.0, 0.0, true );
      ` )
      .withFragmentLogic( `
        vec4 tex = texture( u_texture, v_uv );
        fragColor = vec4( tex.rgb * v_alpha, tex.a * v_alpha );
      ` ) );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const texture = this.createTexture( ImagesHolder.getImage( ImgID.CoinAtlas ), gl, {
      min_filter: gl.LINEAR,
      mag_filter: gl.LINEAR,
      generate_mipmaps: false,
    } );
    this.textures.push( { location: "u_texture", texture: texture, unit: 0 } );
  }

  protected override onBeforeRender( gl: WebGL2RenderingContext ): void
  {
    gl.uniform1f( this.shader.getLocation( gl, "u_scale" ), LayoutScaler.getScale() );
  }

  public spawnCoin(
    start_pos: Vector2,
    precomputed: CatmulromPrecomputed,
    size: number,
    angle: number,
    anim_offset: number,
    duration: number,
    start_time: number,
    ease?: ( t: number ) => number
  ): void
  {
    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 ) return;

    this.precomputed_list[idx] = precomputed;
    this.anim_offsets[idx] = anim_offset;
    this.ease_funcs[idx] = ease;

    const offset = idx * this.buffer.floats_per_instance;
    const gpu_data = this.buffer.getDataArray();

    gpu_data[offset + this.attr_offset.a_position] = start_pos.x;
    gpu_data[offset + this.attr_offset.a_position + 1] = start_pos.y;
    gpu_data[offset + this.attr_offset.a_size] = size;
    gpu_data[offset + this.attr_offset.a_angle] = angle;
    gpu_data[offset + this.attr_offset.a_frame] = anim_offset % 16;
    gpu_data[offset + this.attr_offset.a_timing] = start_time;
    gpu_data[offset + this.attr_offset.a_timing + 1] = duration;
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, progress: number, now: number, idx: number ): boolean
  {
    const precomputed = this.precomputed_list[idx];
    if ( precomputed )
    {
      const ease_fn = this.ease_funcs[idx];
      const t = ease_fn ? ease_fn( progress ) : progress;
      const pos = precomputed.catmullRomAtT( t );
      gpu_data[offset + this.attr_offset.a_position] = pos.x;
      gpu_data[offset + this.attr_offset.a_position + 1] = pos.y;
    }

    const elapsed = max2( 0, now - this.start_times[idx] );
    let frame = Math.floor( elapsed * ( VfxCoinFlyBatch.SPIN_ANIMATION_FPS / 1000 ) + ( this.anim_offsets[idx] || 0 ) ) % 16;
    
    // Якщо кадри в атласі розміщені вертикально по колонках (Column-Major),
    // перераховуємо індекс у сітку getSpriteUV (Row-Major)
    if ( VfxCoinFlyBatch.IS_COLUMN_MAJOR )
    {
      frame = ( frame % 4 ) * 4 + Math.floor( frame / 4 );
    }

    gpu_data[offset + this.attr_offset.a_frame] = frame;

    return true;
  }

  protected override onSwapInstance( idx_a: number, idx_b: number ): void
  {
    const temp_p = this.precomputed_list[idx_a];
    this.precomputed_list[idx_a] = this.precomputed_list[idx_b];
    this.precomputed_list[idx_b] = temp_p;

    const temp_o = this.anim_offsets[idx_a];
    this.anim_offsets[idx_a] = this.anim_offsets[idx_b];
    this.anim_offsets[idx_b] = temp_o;

    const temp_e = this.ease_funcs[idx_a];
    this.ease_funcs[idx_a] = this.ease_funcs[idx_b];
    this.ease_funcs[idx_b] = temp_e;
  }
}
